import { Dispatch, MutableRefObject, SetStateAction } from 'react';

import { Button, Checkbox, Divider, Input, Radio, RadioGroup, Text, ValidationMessage } from '@thefarmersfront/kitchen';
import { isDeepEqual } from '@thefarmersfront/kitchen-utensil';

import { FormField } from '~/components/common';

import { useKakaoPostcodeSearch } from '~/hooks/common';
import { validateNumberAndHyphenOnly } from '~/utils';

import { DEFAULT_INPUT_STATUS } from '~/constants';

import type {
  TypeSupplierAddFormStatus,
  TypeSupplierAddFormValue,
  TypeVenderSupplierAddErrorFieldRefs,
} from '~/types/vender.types';

import styles from './VenderSupplierAddModalShippingAddressInfoSection.module.scss';

type TypeVenderSupplierAddModalShippingAddressInfoSectionProps = {
  errorFieldRefs: MutableRefObject<
    Pick<
      TypeVenderSupplierAddErrorFieldRefs,
      | 'pickupType'
      | 'kurlyPickupZipCode'
      | 'kurlyPickupAddress'
      | 'kurlyPickupContactNumber'
      | 'milkRunPickupZipCode'
      | 'milkRunPickupAddress'
      | 'milkRunPickupContactNumber'
    >
  >;
  venderSupplierAddForm: TypeSupplierAddFormValue;
  venderSupplierAddFormStatus: TypeSupplierAddFormStatus;
  setVenderSupplierAddForm: Dispatch<SetStateAction<TypeSupplierAddFormValue>>;
  setVenderSupplierAddFormStatus: Dispatch<SetStateAction<TypeSupplierAddFormStatus>>;
};

/**
 * [화주사 정보 관리 > 공급사 관리 > 공급사 등록 모달] 출고지 정보 영역
 */
const VenderSupplierAddModalShippingAddressInfoSection = ({
  errorFieldRefs,
  venderSupplierAddForm,
  venderSupplierAddFormStatus,
  setVenderSupplierAddForm,
  setVenderSupplierAddFormStatus,
}: TypeVenderSupplierAddModalShippingAddressInfoSectionProps) => {
  const { handleZipCodeSearchOpen: handleKurlyPickupZipCodeSearchOpen } = useKakaoPostcodeSearch({
    onComplete: (zipCode, address) => {
      setVenderSupplierAddForm((prev) => ({
        ...prev,
        kurlyPickupInfo: { zipCode, address, detailAddress: '', contactNumber: '' },
      }));
    },
  });

  const { handleZipCodeSearchOpen: handleMilkRunPickupZipCodeSearchOpen } = useKakaoPostcodeSearch({
    onComplete: (zipCode, address) => {
      setVenderSupplierAddForm((prev) => ({
        ...prev,
        milkRunPickupInfo: { zipCode, address, detailAddress: '', contactNumber: '' },
      }));
    },
  });

  const handleChangeValue = <K extends keyof TypeSupplierAddFormValue>(key: K, value: TypeSupplierAddFormValue[K]) => {
    setVenderSupplierAddForm({ ...venderSupplierAddForm, [key]: value });
  };

  const handleChangeStatusToDefault = (key: keyof TypeSupplierAddFormStatus | (keyof TypeSupplierAddFormStatus)[]) => {
    if (Array.isArray(key)) {
      const newStatus = key.reduce(
        (acc, curr) => {
          acc[curr] = DEFAULT_INPUT_STATUS;
          return acc;
        },
        { ...venderSupplierAddFormStatus }
      );

      setVenderSupplierAddFormStatus(newStatus);
    } else {
      setVenderSupplierAddFormStatus({ ...venderSupplierAddFormStatus, [key]: DEFAULT_INPUT_STATUS });
    }
  };

  const handleCopyKurlyStatusToMilkRun = () => {
    setVenderSupplierAddFormStatus((prev) => ({
      ...prev,
      milkRunPickupZipCode: prev.kurlyPickupZipCode,
      milkRunPickupAddress: prev.kurlyPickupAddress,
      milkRunPickupContactNumber: prev.kurlyPickupContactNumber,
    }));
  };

  return (
    <div className={styles['shipping-address-info-section']}>
      <Text className={styles['title']} size="xxlarge" weight="bold">
        출고지 정보
      </Text>
      <div className={styles['section-container']}>
        <FormField
          label="픽업 서비스"
          required
          tooltip={{ description: '입고 요청 시 픽업 서비스를 이용하려면\n출고지 정보를 등록해야 합니다.' }}
          contents={
            <RadioGroup
              className={styles['radio-group']}
              name="use-pickup-service"
              size="medium"
              value={venderSupplierAddForm.isUsePickup ? 'Y' : 'N'}
              onChange={(e) => handleChangeValue('isUsePickup', e.target.value === 'Y')}
            >
              <Radio value="Y" label="사용" />
              <Radio value="N" label="사용안함" />
            </RadioGroup>
          }
        />
        {venderSupplierAddForm.isUsePickup && (
          <FormField
            ref={(el) => (errorFieldRefs.current.pickupType = el)}
            label="픽업 유형"
            required
            contents={
              <div className={styles['checkbox-group']}>
                <div className={styles['checkbox-wrapper']}>
                  <Checkbox
                    size="medium"
                    label="컬리픽업"
                    checked={venderSupplierAddForm.isUseKurlyPickup}
                    status={venderSupplierAddFormStatus.pickupType.status}
                    onChange={(e) => {
                      handleChangeValue('isUseKurlyPickup', e.target.checked);
                      handleChangeStatusToDefault('pickupType');
                    }}
                  />
                  <Checkbox
                    size="medium"
                    label="밀크런픽업"
                    checked={venderSupplierAddForm.isUseMilkRunPickup}
                    status={venderSupplierAddFormStatus.pickupType.status}
                    onChange={(e) => {
                      handleChangeValue('isUseMilkRunPickup', e.target.checked);
                      handleChangeStatusToDefault('pickupType');
                    }}
                  />
                </div>
                {venderSupplierAddFormStatus.pickupType.status === 'error' && (
                  <ValidationMessage
                    className={styles['checkbox-validation-message']}
                    message={venderSupplierAddFormStatus.pickupType.message ?? ''}
                  />
                )}
              </div>
            }
          />
        )}
      </div>
      {venderSupplierAddForm.isUsePickup && venderSupplierAddForm.isUseKurlyPickup && (
        <>
          <Divider color="gray100" />
          <div className={styles['section-container']}>
            <Text className={styles['section-title']} size="large" weight="bold">
              컬리픽업 출고지
            </Text>
            <FormField
              label="우편번호"
              required
              contents={
                <>
                  <Input
                    ref={(el) => (errorFieldRefs.current.kurlyPickupZipCode = el)}
                    type="text"
                    size="small"
                    maxWidth="229px"
                    disabled
                    status={venderSupplierAddFormStatus.kurlyPickupZipCode.status}
                    validationMessage={venderSupplierAddFormStatus.kurlyPickupZipCode.message ?? ''}
                    value={venderSupplierAddForm.kurlyPickupInfo.zipCode}
                  />
                  <Button
                    className={styles['address-search-button']}
                    color="secondary"
                    size="small"
                    onClick={() => {
                      handleKurlyPickupZipCodeSearchOpen();
                      handleChangeStatusToDefault(['kurlyPickupZipCode', 'kurlyPickupAddress']);
                    }}
                  >
                    검색
                  </Button>
                </>
              }
            />
            <FormField
              label="기본주소"
              required
              contents={
                <Input
                  ref={(el) => (errorFieldRefs.current.kurlyPickupAddress = el)}
                  type="text"
                  size="small"
                  maxWidth="309px"
                  disabled
                  status={venderSupplierAddFormStatus.kurlyPickupAddress.status}
                  validationMessage={venderSupplierAddFormStatus.kurlyPickupAddress.message ?? ''}
                  value={venderSupplierAddForm.kurlyPickupInfo.address}
                />
              }
            />
            <FormField
              label="상세주소"
              contents={
                <Input
                  type="text"
                  size="small"
                  maxWidth="309px"
                  placeholder="입력"
                  value={venderSupplierAddForm.kurlyPickupInfo.detailAddress}
                  onChange={(e) =>
                    handleChangeValue('kurlyPickupInfo', {
                      ...venderSupplierAddForm.kurlyPickupInfo,
                      detailAddress: e.target.value,
                    } as TypeSupplierAddFormValue['kurlyPickupInfo'])
                  }
                  onBlur={(e) =>
                    handleChangeValue('kurlyPickupInfo', {
                      ...venderSupplierAddForm.kurlyPickupInfo,
                      detailAddress: e.target.value.trim(),
                    } as TypeSupplierAddFormValue['kurlyPickupInfo'])
                  }
                />
              }
            />
            <FormField
              label="연락처"
              required
              contents={
                <Input
                  ref={(el) => (errorFieldRefs.current.kurlyPickupContactNumber = el)}
                  type="text"
                  size="small"
                  maxWidth="229px"
                  placeholder="- 포함 입력"
                  status={venderSupplierAddFormStatus.kurlyPickupContactNumber.status}
                  validationMessage={venderSupplierAddFormStatus.kurlyPickupContactNumber.message ?? ''}
                  value={venderSupplierAddForm.kurlyPickupInfo.contactNumber}
                  onChange={(e) => {
                    if (!validateNumberAndHyphenOnly(e.target.value)) return;

                    handleChangeValue('kurlyPickupInfo', {
                      ...venderSupplierAddForm.kurlyPickupInfo,
                      contactNumber: e.target.value,
                    } as TypeSupplierAddFormValue['kurlyPickupInfo']);
                    handleChangeStatusToDefault('kurlyPickupContactNumber');
                  }}
                  onBlur={(e) =>
                    handleChangeValue('kurlyPickupInfo', {
                      ...venderSupplierAddForm.kurlyPickupInfo,
                      contactNumber: e.target.value.trim(),
                    } as TypeSupplierAddFormValue['kurlyPickupInfo'])
                  }
                  onClick={() => handleChangeStatusToDefault('kurlyPickupContactNumber')}
                />
              }
            />
          </div>
        </>
      )}
      {venderSupplierAddForm.isUsePickup && venderSupplierAddForm.isUseMilkRunPickup && (
        <>
          <Divider color="gray100" />
          <div className={styles['section-container']}>
            <Text className={styles['section-title']} size="large" weight="bold">
              밀크런픽업 출고지
            </Text>
            <FormField
              label="우편번호"
              required
              contents={
                <>
                  <Input
                    ref={(el) => (errorFieldRefs.current.milkRunPickupZipCode = el)}
                    type="text"
                    size="small"
                    maxWidth="229px"
                    disabled
                    status={venderSupplierAddFormStatus.milkRunPickupZipCode.status}
                    validationMessage={venderSupplierAddFormStatus.milkRunPickupZipCode.message ?? ''}
                    value={venderSupplierAddForm.milkRunPickupInfo.zipCode}
                  />
                  <Button
                    className={styles['address-search-button']}
                    color="secondary"
                    size="small"
                    onClick={() => {
                      handleMilkRunPickupZipCodeSearchOpen();
                      handleChangeStatusToDefault(['milkRunPickupZipCode', 'milkRunPickupAddress']);
                    }}
                  >
                    검색
                  </Button>
                  {venderSupplierAddForm.isUseKurlyPickup && (
                    <Checkbox
                      className={styles['same-address-checkbox']}
                      size="medium"
                      label="컬리픽업과 동일"
                      checked={
                        (Object.values(venderSupplierAddForm.kurlyPickupInfo).some((value) => value.length > 0) ||
                          Object.values(venderSupplierAddForm.milkRunPickupInfo).some((value) => value.length > 0)) &&
                        isDeepEqual(venderSupplierAddForm.kurlyPickupInfo, venderSupplierAddForm.milkRunPickupInfo)
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleChangeValue('milkRunPickupInfo', venderSupplierAddForm.kurlyPickupInfo);
                          handleCopyKurlyStatusToMilkRun();
                        } else {
                          handleChangeValue('milkRunPickupInfo', {
                            zipCode: '',
                            address: '',
                            detailAddress: '',
                            contactNumber: '',
                          });
                          handleChangeStatusToDefault([
                            'milkRunPickupZipCode',
                            'milkRunPickupAddress',
                            'milkRunPickupContactNumber',
                          ]);
                        }
                      }}
                    />
                  )}
                </>
              }
            />
            <FormField
              label="기본주소"
              required
              contents={
                <Input
                  ref={(el) => (errorFieldRefs.current.milkRunPickupAddress = el)}
                  type="text"
                  size="small"
                  maxWidth="309px"
                  disabled
                  status={venderSupplierAddFormStatus.milkRunPickupAddress.status}
                  validationMessage={venderSupplierAddFormStatus.milkRunPickupAddress.message ?? ''}
                  value={venderSupplierAddForm.milkRunPickupInfo.address}
                />
              }
            />
            <FormField
              label="상세주소"
              contents={
                <Input
                  type="text"
                  size="small"
                  maxWidth="309px"
                  placeholder="입력"
                  value={venderSupplierAddForm.milkRunPickupInfo.detailAddress}
                  onChange={(e) =>
                    handleChangeValue('milkRunPickupInfo', {
                      ...venderSupplierAddForm.milkRunPickupInfo,
                      detailAddress: e.target.value,
                    } as TypeSupplierAddFormValue['milkRunPickupInfo'])
                  }
                  onBlur={(e) =>
                    handleChangeValue('milkRunPickupInfo', {
                      ...venderSupplierAddForm.milkRunPickupInfo,
                      detailAddress: e.target.value.trim(),
                    } as TypeSupplierAddFormValue['milkRunPickupInfo'])
                  }
                />
              }
            />
            <FormField
              label="연락처"
              required
              contents={
                <Input
                  ref={(el) => (errorFieldRefs.current.milkRunPickupContactNumber = el)}
                  type="text"
                  size="small"
                  maxWidth="229px"
                  placeholder="- 포함 입력"
                  status={venderSupplierAddFormStatus.milkRunPickupContactNumber.status}
                  validationMessage={venderSupplierAddFormStatus.milkRunPickupContactNumber.message ?? ''}
                  value={venderSupplierAddForm.milkRunPickupInfo.contactNumber}
                  onChange={(e) => {
                    if (!validateNumberAndHyphenOnly(e.target.value)) return;

                    handleChangeValue('milkRunPickupInfo', {
                      ...venderSupplierAddForm.milkRunPickupInfo,
                      contactNumber: e.target.value,
                    } as TypeSupplierAddFormValue['milkRunPickupInfo']);
                    handleChangeStatusToDefault('milkRunPickupContactNumber');
                  }}
                  onBlur={(e) =>
                    handleChangeValue('milkRunPickupInfo', {
                      ...venderSupplierAddForm.milkRunPickupInfo,
                      contactNumber: e.target.value.trim(),
                    } as TypeSupplierAddFormValue['milkRunPickupInfo'])
                  }
                  onClick={() => handleChangeStatusToDefault('milkRunPickupContactNumber')}
                />
              }
            />
          </div>
        </>
      )}
    </div>
  );
};

export default VenderSupplierAddModalShippingAddressInfoSection;
